import React, { useEffect, useState } from "react";
import {
  Padding,
  Layout,
  Sheet,
  Text,
  Margin,
  Modal,
  Button,
  Tab,
  MultiSearch,
  Input,
} from "@kidslivesafe/sc-react";

import MostRecentReport from "./MostRecentReport";
import ActivityHistoryContainer from "./ActivityHistoryContainer";
import MonitoringContainer from "./MonitoringContainer";
import SpacerMultiSearch from "../../components/UIComponents/SpacerMultiSearch/SpacerMultiSearch";
import IdProtect from "./IdProtectDash";
import ProfileProtectionDash from "./ProfileProtectionDash";
import SpacerMonitoring from "./SpacerMonitoring";

import "./dashboard.css";
import {
  selectIdProtectData,
  selectMemberData,
  selectAccountType,
  AccountType,
  selectIsPIS75Variant,
} from "../../features/authentication/authenticationSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";

import {
  createCustomerRedisTokenAsync,
  selectAdminStatus,
  sendEmailVerificationAsync,
} from "../../features/admin/adminSlice";
import { selectBrandInfo } from "../../features/brand/brandSlice";
import { useMultiSearchProps } from "../../hooks/multiSearchPropsHook";
import { selectEmailVerificationRequired } from "../../features/admin/adminSlice";

import { scrollToHashElement } from "../../utils";
import { useCurrentBreakpoint } from "@kidslivesafe/sc-foundation";

import {
  selectRenameReport,
  selectToggleMonitoringModal,
  setToggleMonitoringModal,
  updateNeighborhoodWatchMonitoringAreaAsync,
  updatePropertyMonitoringAsync,
} from "../../features/monitoring/monitoringSlice";

import OnboardingModal from "./OnboardingModal";
import RetryModal from "./RetryModal";
import VehicleNoResultsModal from "./VehicleNoResultsModal";

import ListDashboardContainer from "./ListsContainer";
import NotesDashboardContainer from "./NotesContainer";
import PauseBanner from "../../components/UIComponents/PauseComponents/PauseBanner";
import ReactivateModal from "./ReactivateModal";
import AccelerateModal from "./AccelerateModal";
import SecondaryAccelerateModal from "./SecondaryAccelerateModal";

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();

  const brandInfo = useAppSelector(selectBrandInfo);
  const idProtectData = useAppSelector(selectIdProtectData);
  const isPIS75Variant = useAppSelector(selectIsPIS75Variant);
  const { customerId } = useAppSelector(selectMemberData);
  const adminStatus = useAppSelector(selectAdminStatus);
  const accountType = useAppSelector(selectAccountType);
  const emailVerificationRequired = useAppSelector(
    selectEmailVerificationRequired,
  );
  const [nickname, setNickname] = useState<string>("");
  const [nicknameError, setNickNameError] = useState<boolean>(false);

  const renameReport = useAppSelector(selectRenameReport);

  const monitoringModal = useAppSelector(selectToggleMonitoringModal);

  const modalLoading = adminStatus === "loading"; // talk to cody why its in useEffect
  const currentBreakpoint = useCurrentBreakpoint();

  const [emailSent, setEmailSent] = useState<boolean>(false);

  const [tabs, setTabs] = React.useState<Tab[]>([
    "people",
    "property",
    "phone",
    "social",
    "foreclosure",
  ]);

  const whichTabs = (type: AccountType | null) => {
    switch (type) {
      case "vehicle":
        setTabs(["vehicle", "people", "property", "phone"]);
        break;
      case "aamva":
        setTabs(["vehicle"]);
        break; //aamva truncated view
      default:
        setTabs(["people", "property", "phone", "social", "foreclosure"]);
        break;
    }
  };

  useEffect(() => {
    whichTabs(accountType);
  }, [accountType]);

  const multiSearchProps = useMultiSearchProps(
    {
      footer: false,
      resetInput: true,
      buttonText: "SEARCH NOW",
      tabs: tabs,
      vehicleModifactions: { showVehicleRadioInput: true },
      propertyModifactions: {
        showPropertyRadioInput: true,
      },
    },
    "dashboard",
  );

  //if there is an anchor hash, will scroll
  useEffect(() => {
    const scrollHeight = currentBreakpoint === "desktop" ? 75 : 80;

    // Scroll only if there's a hash in the URL
    const handleScroll = () => {
      // error occuriong where querySelector trying to be performed when it shouldnt inside of scrollToHashElement
      // ex. '#!/login'. '!' throws error.
      if (window.location.hash && !window.location.hash.includes("!")) {
        // Ensure a delay so that the page is fully rendered
        setTimeout(() => {
          scrollToHashElement(scrollHeight);
        }, 700);
      }
    };

    // Call on initial render or breakpoint change
    handleScroll();

    // Optional: Handle hash change events dynamically
    window.addEventListener("hashchange", handleScroll);

    return () => {
      // Cleanup the event listener
      window.removeEventListener("hashchange", handleScroll);
    };
  }, [currentBreakpoint]); // Re-run effect when breakpoint changes

  const handleSendEmailVerification = async () => {
    const createCustomerRedisTokenAsyncResponse = await dispatch(
      createCustomerRedisTokenAsync(),
    );
    if (
      createCustomerRedisTokenAsync.fulfilled.match(
        createCustomerRedisTokenAsyncResponse,
      )
    ) {
      const token = createCustomerRedisTokenAsyncResponse.payload.token;
      const sendEmailVerificationResponse = await dispatch(
        sendEmailVerificationAsync({ customerId: customerId, token: token }),
      );
      if (
        sendEmailVerificationAsync.fulfilled.match(
          sendEmailVerificationResponse,
        )
      ) {
        setEmailSent(true);
      }
    }
  };

  const handleSubmitNickName = (nickname: string) => {
    if (nickname.length > 15) {
      setNickNameError(true);
    } else {
      setNickNameError(false);
    }
  };

  return (
    <>
      <Sheet>
        {accountType === "aamva" ? (
          <Padding
            space="none"
            breakpointStyles={{ desktop: { left: "md", right: "md" } }}
          >
            <MultiSearch {...multiSearchProps} />
          </Padding>
        ) : null}
        <Padding top="md" bottom="md">
          <Layout type="flex" flexProps={{ direction: "column", gap: "md" }}>
            <MostRecentReport />
            {accountType === "aamva" ? null : (
              //for aamva truncated view
              <>
                <SpacerMultiSearch
                  options={
                    brandInfo.acronym === "pdc"
                      ? ["people", "property", "phone", "vehicle"]
                      : ["people", "property", "phone", "social"]
                  }
                />

                {idProtectData.isIdProtectAvailable ||
                idProtectData.idProtectMember ? (
                  <Padding left="md" right="md">
                    <IdProtect />
                  </Padding>
                ) : null}

                {isPIS75Variant ? (
                  <Padding left="md" right="md">
                    <ProfileProtectionDash />
                  </Padding>
                ) : null}
                <ListDashboardContainer />
                <NotesDashboardContainer />
                <MonitoringContainer />
              </>
            )}
            <ActivityHistoryContainer />
          </Layout>
        </Padding>
      </Sheet>
      {accountType === "aamva" ? null : <SpacerMonitoring />}
      <ReactivateModal />
      <Modal
        open={emailVerificationRequired}
        setOpen={() => {}}
        size="fit-content"
        xClose
      >
        <Padding space="md">
          <Margin top="xxl" bottom="xxl">
            {!emailSent ? (
              <Layout
                type="flex"
                flexProps={{
                  direction: "column",
                  gap: "lg",
                  alignItems: "center",
                }}
              >
                <Layout
                  type="flex"
                  flexProps={{ direction: "column", gap: "lg" }}
                >
                  <Text tag="h2" textAlign="center">
                    {" "}
                    Verify Email
                  </Text>
                  <Text textAlign="center" size="sm">
                    You must verify your email to access more reports. Please
                    click OK and we will send you a verification email.
                  </Text>
                </Layout>
                <Button
                  buttonText="Send Email"
                  handleClick={handleSendEmailVerification}
                  clicked={modalLoading}
                  loading={modalLoading}
                />
              </Layout>
            ) : (
              <Layout
                type="flex"
                flexProps={{ direction: "column", gap: "lg" }}
              >
                <Text tag="h2" textAlign="center">
                  {" "}
                  Verify Email
                </Text>
                <Text textAlign="center" size="sm">
                  Your Email has been sent.
                </Text>
              </Layout>
            )}
          </Margin>
        </Padding>
      </Modal>
      <Modal
        open={monitoringModal}
        setOpen={() => {
          dispatch(setToggleMonitoringModal(!monitoringModal));
          setNickname("");
        }}
        size="fit-content"
        xClose
        outSideClose
      >
        <Padding space="md">
          <Layout
            type="flex"
            flexProps={{ direction: "column", gap: "lg", alignItems: "center" }}
          >
            <Text tag="h2">Set New Name</Text>
            <Text size="xs" tag="i">
              Please use 15 characters maximum
            </Text>
            <Layout
              type="flex"
              flexProps={{
                gap: "sm",
                direction: "column",
                alignItems: "center",
              }}
            >
              <Input
                id="neighborhood-report__monitoring-modal"
                value={nickname}
                handleChange={input => {
                  setNickname(input);
                  handleSubmitNickName(input);
                }}
                type="text"
                placeholder={
                  renameReport && renameReport.type === "property"
                    ? "Property"
                    : "Neighborhood"
                }
                fill="fill"
                error={nicknameError}
              />
              <Button
                buttonText="Set Name"
                fill="fill"
                disabled={nicknameError}
                handleClick={() => {
                  if (renameReport) {
                    if (renameReport.type === "neighborhood") {
                      dispatch(
                        updateNeighborhoodWatchMonitoringAreaAsync({
                          address: renameReport.address,
                          addressLine2: renameReport.addressLine2,
                          city: renameReport.city,
                          state: renameReport.state,
                          zipCode: renameReport.zipCode,
                          nickname: nickname,
                          radius: 5,
                          id: renameReport.id,
                          latestOffendersCount:
                            renameReport?.latestOffendersCount,
                        }),
                      );
                    } else if (renameReport.type === "property") {
                      dispatch(
                        updatePropertyMonitoringAsync({
                          addressLine1: renameReport.addressLine1,
                          addressLine2: renameReport.addressLine2,
                          city: renameReport.city,
                          state: renameReport.state,
                          zip: renameReport.zip,
                          nickname: nickname,
                        }),
                      );
                    }
                  }
                  dispatch(setToggleMonitoringModal(!monitoringModal));
                  setNickname("");
                }}
              />
            </Layout>
          </Layout>
        </Padding>
      </Modal>
      {/* Onboarding Modal - PIS63 */}
      <OnboardingModal />
      <RetryModal />
      <AccelerateModal />
      <SecondaryAccelerateModal />
      <PauseBanner />
      <VehicleNoResultsModal />
    </>
  );
};

export default Dashboard;
